<script setup lang="ts">
/**
 * Compact list of per-conversation uploads for the General assistant.
 *
 * Rendered just above the composer. Each row shows the filename,
 * size + token estimate, and a delete button. Empty state stays
 * collapsed so the composer doesn't shift when the user hasn't
 * uploaded anything yet.
 */
import { computed, onUnmounted, ref, watch } from "vue";

import { acquireToken, USE_REAL_AUTH } from "../auth/msal";
import { useConfirm } from "../composables/useConfirm";

const { confirm } = useConfirm();

const props = defineProps<{
  conversationId: number | null;
  devUser: string;
  /** Bumped by parent after a successful upload — triggers refetch. */
  refreshKey?: number;
}>();

type Upload = {
  source: string;
  filename: string;
  rel_path?: string;
  role?: string;
  mime_type: string;
  estimated_tokens: number;
  chunk_count: number;
  byte_size: number;
  created_at: string;
  /** pending | indexing | ready | failed — see the backend upload endpoint. */
  index_status?: string;
  index_error?: string;
};

const docs = ref<Upload[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);

async function authHeaders(): Promise<Record<string, string>> {
  const h: Record<string, string> = { Accept: "application/json" };
  if (USE_REAL_AUTH) {
    const t = await acquireToken();
    if (t) h["Authorization"] = `Bearer ${t.token}`;
  } else if (props.devUser) {
    h["X-Dev-User"] = props.devUser;
  }
  return h;
}

async function load() {
  if (props.conversationId === null) {
    docs.value = [];
    return;
  }
  loading.value = true;
  error.value = null;
  try {
    const r = await fetch(
      `${import.meta.env.VITE_API_BASE ?? ""}/api/conversations/${props.conversationId}/uploads`,
      { headers: await authHeaders(), cache: "no-store" },
    );
    if (!r.ok) throw new Error(`${r.status}`);
    docs.value = (await r.json()) as Upload[];
  } catch (e: unknown) {
    error.value = e instanceof Error ? e.message : String(e);
  } finally {
    loading.value = false;
  }
}

watch(() => [props.conversationId, props.refreshKey], load, {
  immediate: true,
});

// Indexing finishes AFTER the upload response, so the panel would sit on
// "Preparing for search" forever without re-reading. Poll only while
// something is actually in flight, and stop the moment nothing is — an
// idle conversation must not sit there hitting the API.
const POLL_MS = 4000;
let pollTimer: ReturnType<typeof setInterval> | null = null;

function stopPolling() {
  if (pollTimer !== null) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

const isSettling = computed(() =>
  docs.value.some(
    (d) => d.index_status === "pending" || d.index_status === "indexing",
  ),
);

watch(isSettling, (settling) => {
  if (settling && pollTimer === null) {
    pollTimer = setInterval(load, POLL_MS);
  } else if (!settling) {
    stopPolling();
  }
});

onUnmounted(stopPolling);

async function remove(source: string, role?: string) {
  if (props.conversationId === null) return;
  if (role === "source") {
    error.value = "A] Udbudsmateriale files cannot be deleted.";
    return;
  }
  const ok = await confirm({
    title: "Delete this upload?",
    message:
      "The file will be removed from the conversation and from the index.",
    confirmLabel: "Delete",
    danger: true,
  });
  if (!ok) return;
  try {
    const r = await fetch(
      `${import.meta.env.VITE_API_BASE ?? ""}/api/conversations/${props.conversationId}/uploads/${encodeURIComponent(source)}`,
      { method: "DELETE", headers: await authHeaders() },
    );
    if (!r.ok && r.status !== 204) throw new Error(`${r.status}`);
    docs.value = docs.value.filter((d) => d.source !== source);
  } catch (e: unknown) {
    error.value = e instanceof Error ? e.message : String(e);
  }
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** Last path segment. A folder upload carries `a/b/c/report.pdf`, and the
 *  leading directories are what push a chip past any sane width — the full
 *  path stays in the tooltip. */
function baseName(path: string): string {
  const parts = path.split("/").filter(Boolean);
  return parts.length ? parts[parts.length - 1] : path;
}

/** Everything the chip cannot show, on hover. */
function chipTitle(d: Upload): string {
  const bits = [d.rel_path || d.filename];
  if (d.role === "source") bits.push("A] Udbudsmateriale (immutable)");
  const status = d.index_status ?? "ready";
  const chunks = status === "ready" ? `${d.chunk_count} chunks` : status;
  bits.push(
    `${fmtBytes(d.byte_size)} · ${chunks} · ~${d.estimated_tokens.toLocaleString()} tokens`,
  );
  // Indexing runs in the background, so chunk_count is 0 until it lands.
  // Only "ready with zero chunks" actually means nothing was extracted —
  // testing chunk_count alone would flag every fresh upload as broken.
  if (status === "pending" || status === "indexing") {
    bits.push("Preparing for search — you can ask about it now");
  } else if (status === "failed") {
    bits.push(
      d.index_error
        ? `Could not be prepared for search: ${d.index_error}`
        : "Could not be prepared for search",
    );
  } else if (d.chunk_count === 0) {
    bits.push("No searchable text was extracted");
  }
  return bits.join("\n");
}

const hasDocs = computed(() => docs.value.length > 0);
</script>

<template>
  <aside v-if="hasDocs" class="upload-panel">
    <details class="upload-panel__details" open>
      <summary class="upload-panel__summary">
        <span>📎</span>
        Uploaded ({{ docs.length }})
      </summary>
      <!-- Chips, not rows. Each upload used to be a full-width line, so ten
           files pushed the composer ~340px down the screen and off a laptop
           display entirely. The detail (size, chunks, tokens) is what a chip
           cannot show — it moves to the tooltip, where it stays available
           without costing vertical space on every file. -->
      <ul class="upload-panel__list">
        <li
          v-for="d in docs"
          :key="d.source"
          class="upload-panel__chip"
          :class="{ 'upload-panel__chip--locked': d.role === 'source' }"
          :title="chipTitle(d)"
        >
          <span class="upload-panel__name">{{
            baseName(d.rel_path || d.filename)
          }}</span>
          <button
            v-if="d.role !== 'source'"
            type="button"
            class="upload-panel__delete"
            :aria-label="`Remove ${d.filename}`"
            @click="remove(d.source, d.role)"
          >
            ×
          </button>
          <span
            v-else
            class="upload-panel__lock"
            title="A] Udbudsmateriale is immutable"
            >🔒</span
          >
        </li>
      </ul>
    </details>
    <div v-if="error" class="upload-panel__error">{{ error }}</div>
  </aside>
</template>

<style scoped>
.upload-panel {
  margin-top: var(--space-2);
}
.upload-panel__details > summary {
  cursor: pointer;
  font-size: 0.85rem;
  color: var(--color-muted);
  list-style: revert;
  display: flex;
  gap: 0.4rem;
  align-items: center;
}
/* Wrapping chips. Height is capped at roughly three rows so that even a
   30-file folder upload cannot walk the composer off the bottom of a small
   laptop screen; past that the strip scrolls. */
.upload-panel__list {
  list-style: none;
  margin: 0.5rem 0 0 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  max-height: 6.75rem;
  overflow-y: auto;
  overscroll-behavior: contain;
}
.upload-panel__chip {
  display: inline-flex;
  align-items: center;
  gap: 0.15rem;
  max-width: 16rem;
  padding: 0.2rem 0.2rem 0.2rem 0.55rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-pill, 999px);
  background: var(--color-surface);
  font-size: 0.8rem;
  line-height: 1.4;
}
.upload-panel__chip--locked {
  padding-right: 0.35rem;
}
.upload-panel__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.upload-panel__delete {
  background: transparent;
  border: 0;
  color: var(--color-muted);
  font-size: 1rem;
  line-height: 1;
  cursor: pointer;
  padding: 0.1rem 0.35rem;
  border-radius: 999px;
}
.upload-panel__delete:hover {
  background: var(--color-border);
  color: var(--color-text, inherit);
}
.upload-panel__lock {
  font-size: 0.85rem;
  padding: 0 0.35rem;
}
.upload-panel__error {
  margin-top: 0.25rem;
  font-size: 0.8rem;
  color: var(--color-error);
}
</style>
